/**
 * EntityAuditLog Component
 * Displays audit log history for a specific entity (customer, vehicle, etc.)
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History, ChevronDown, ChevronUp, Clock, User, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, Skeleton, Badge, EmptyState } from '../ui';
import { auditApi } from '../../services/auditApi';
import type { AuditLogEntry } from '@leal-crm/shared';

interface EntityAuditLogProps {
  entityType: 'customer' | 'vehicle' | 'booking';
  entityId: string;
  title?: string;
  limit?: number;
}

function formatDate(timestampSeconds: number): string {
  const date = new Date(timestampSeconds * 1000);
  return date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelativeDate(timestampSeconds: number): string {
  const now = Date.now();
  const date = timestampSeconds * 1000;
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} dias`;
  if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} semanas`;
  return `Hace ${Math.floor(diffDays / 30)} meses`;
}

function getEventColor(action: string): { bg: string; text: string } {
  if (action.includes('created')) return { bg: 'bg-green-100', text: 'text-green-700' };
  if (action.includes('updated')) return { bg: 'bg-blue-100', text: 'text-blue-700' };
  if (action.includes('deleted')) return { bg: 'bg-red-100', text: 'text-red-700' };
  return { bg: 'bg-gray-100', text: 'text-gray-700' };
}

function ChangesDiff({ changes }: { changes?: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false);

  if (!changes || Object.keys(changes).length === 0) {
    return <span className="text-muted-foreground text-xs">Sin cambios registrados</span>;
  }

  const { before, after, ...otherDetails } = changes as {
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
    [key: string]: unknown;
  };

  // If we have before/after, show diff
  if (before || after) {
    const changedFields = new Set([
      ...Object.keys(before || {}),
      ...Object.keys(after || {}),
    ]);

    return (
      <div className="mt-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {changedFields.size} campo(s) modificado(s)
        </button>

        {expanded && (
          <div className="mt-2 space-y-1 text-xs">
            {Array.from(changedFields).map((field) => {
              const beforeVal = before?.[field];
              const afterVal = after?.[field];
              if (beforeVal === afterVal) return null;

              return (
                <div key={field} className="flex gap-2 items-start">
                  <span className="font-medium min-w-24">{field}:</span>
                  <div className="flex-1">
                    {beforeVal !== undefined && (
                      <span className="bg-red-50 text-red-700 px-1 rounded line-through mr-1">
                        {String(beforeVal)}
                      </span>
                    )}
                    {afterVal !== undefined && (
                      <span className="bg-green-50 text-green-700 px-1 rounded">
                        {String(afterVal)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Otherwise just show the details
  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        Ver detalles
      </button>

      {expanded && (
        <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-x-auto">
          {JSON.stringify(otherDetails, null, 2)}
        </pre>
      )}
    </div>
  );
}

function AuditLogItem({ entry }: { entry: AuditLogEntry }) {
  const colors = getEventColor(entry.action);

  return (
    <div className="relative flex gap-3 pl-1">
      {/* Icon */}
      <div className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${colors.bg} border border-current/10`}>
        <FileText className={`h-4 w-4 ${colors.text}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Badge className={`${colors.bg} ${colors.text} border-0`}>
                {entry.actionDisplayName}
              </Badge>
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              <User className="h-3 w-3" />
              <span>{entry.actorName || entry.actorEmail || 'Sistema'}</span>
              {entry.ipAddress && (
                <>
                  <span className="text-muted-foreground/50">|</span>
                  <span>{entry.ipAddress}</span>
                </>
              )}
            </div>
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-1" title={formatDate(entry.createdAt)}>
            <Clock className="h-3 w-3" />
            {formatRelativeDate(entry.createdAt)}
          </div>
        </div>

        <ChangesDiff changes={entry.changes} />
      </div>
    </div>
  );
}

export function EntityAuditLog({
  entityType,
  entityId,
  title = 'Historial de cambios',
  limit = 20,
}: EntityAuditLogProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['audit', 'entity', entityType, entityId],
    queryFn: () => auditApi.getEntityLogs(entityType, entityId, { limit }),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" />
            <Skeleton className="h-4 w-32" />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex gap-3 pl-1">
                <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                <div className="flex-1 space-y-2 pb-4">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">Error al cargar historial</p>
        </CardContent>
      </Card>
    );
  }

  const logs = data?.logs || [];

  if (logs.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-medium">{title}</h4>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <EmptyState
            icon={History}
            title="Sin historial"
            description="No hay cambios registrados para este registro"
            compact
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-medium">{title}</h4>
          </div>
          <span className="text-xs text-muted-foreground">
            {data?.total || logs.length} registro(s)
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />

          {/* Entries */}
          <div className="space-y-2">
            {logs.map((entry) => (
              <AuditLogItem key={entry.id} entry={entry} />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
